if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector("form");
        this.variantIdInput.disabled = false;
        this.form.addEventListener("submit", this.onSubmitHandler.bind(this));
        this.cart =
          document.querySelector("cart-notification") ||
          document.querySelector("cart-drawer");
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector("span");
        this.upsellVariantId = this.dataset.upsellVariantId;

        if (document.querySelector("cart-drawer"))
          this.submitButton.setAttribute("aria-haspopup", "dialog");

        this.hideErrors = this.dataset.hideErrors === "true";

        // P0 (2026-07-28): the button is disabled for the duration of an add and
        // re-enabled in .finally(). When the add ends in a navigation (skip-to-
        // checkout), iOS Safari restores this exact page from the back/forward
        // cache with the button still disabled, so every later tap hit the
        // aria-disabled guard below and did nothing at all. Reset on restore.
        window.addEventListener("pageshow", (e) => {
          if (e.persisted) this.resetSubmitState();
        });
      }

      // Return the button to a tappable, non-loading state.
      resetSubmitState() {
        this.error = false;
        this.submitButton.classList.remove("loading");
        this.submitButton.removeAttribute("aria-disabled");
        this.querySelector(".loading__spinner")?.classList.add("hidden");
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute("aria-disabled") === "true") return;

        this.handleErrorMessage();

        this.submitButton.setAttribute("aria-disabled", true);
        this.submitButton.classList.add("loading");
        // Optional chaining: a missing spinner used to throw here and kill the
        // whole handler before a single request went out (silent dead button).
        this.querySelector(".loading__spinner")?.classList.remove("hidden");

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            "sections",
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append("sections_url", window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        /* OWNER FIX 2026-07-30 — the money path. `skipCart` sent the shopper
           straight to /checkout after an add, so the cart drawer never opened and
           they never saw the drawer we built (free gifts, discount field, quiz
           offer, 60-day guarantee). That is both the "I clicked Add to Cart and
           the popup cart didn't appear" bug AND a conversion leak: everything we
           use to justify the price lives in that drawer.
           The rule is now absolute: an add ALWAYS opens the drawer. Checkout only
           ever happens from inside the drawer (see snippets/hr-checkout-gate).
           The theme setting is deliberately ignored rather than removed, so
           re-enabling it is a one-line change if that is ever wanted. */
        this.skipCart = false;

        /* PERF (owner brief 2026-08-05, measured ~193ms on the live add path):
           this pre-flight `/cart.js` GET only exists to decide whether to also
           silently add the upsell product BEFORE the shopper's own item, and
           only matters at all when this product block was configured with an
           upsellVariantId AND the cart is currently empty. On every other add
           (the vast majority -- this PDP has no upsell configured) it was pure
           serial dead time in front of the real `/cart/add` call: a full round
           trip that had to finish before the add the shopper is waiting on
           even started. Skipping it when there is no upsell to consider keeps
           the exact same behaviour for pages that do use it, and removes a
           guaranteed ~200ms of serial network from every other add. */
        const upsellPreflight = this.upsellVariantId
          ? fetch(`${routes.cart_url}.js`)
              .then((response) => response.json())
              .then((cartState) => {
                if (cartState.item_count === 0 && this.upsellVariantId) {
                  return fetch(
                    `${routes.cart_add_url}`,
                    this.getUpsellProductConfig()
                  );
                }
              })
              .catch(() => {})
          : Promise.resolve();

        upsellPreflight
          .then(() => {
            return fetch(`${routes.cart_add_url}`, config);
          })

          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              // The optimistic drawer open (assets/cart-drawer.js
              // showOptimisticAdd, wired from assets/hr-subscription-box.js)
              // has its own 9s self-clearing safety timer, but a genuine
              // add failure (sold out, invalid selling plan) is known right
              // here and should not leave a fake placeholder row waiting
              // out that timer for no reason.
              if (this.cart && typeof this.cart.clearOptimisticAdd === 'function') {
                this.cart.clearOptimisticAdd();
              }
              publish(PUB_SUB_EVENTS.cartError, {
                source: "product-form",
                productVariantId: formData.get("id"),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage =
                this.submitButton.querySelector(".sold-out-message");
              if (!soldOutMessage) return;
              this.submitButton.setAttribute("aria-disabled", true);
              this.submitButtonText.classList.add("hidden");
              soldOutMessage.classList.remove("hidden");
              this.error = true;
              return;
            } else if (this.skipCart) {
              window.location = "/checkout";
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: "product-form",
                productVariantId: formData.get("id"),
                cartData: response,
              });
              // Cart "hooray" 15%-off incentive (assets/hr-cart-hooray.js) listens
              // for this on ANY successful add; it no-ops after the first add of
              // the session (own localStorage flag), so this is safe to dispatch
              // on every add without extra guards here.
              document.dispatchEvent(new CustomEvent("hr:cart-item-added"));
            }
            this.error = false;
            const quickAddModal = this.closest("quick-add-modal");
            if (quickAddModal) {
              document.body.addEventListener(
                "modalClosed",
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
              this.confirmVisibleFeedback();
            }
          })
          .catch((e) => {
            console.error(e);
            // The add may well have gone through before this threw. Never leave
            // the shopper with a silent no-op: send them to the cart so they can
            // see what happened.
            if (this.cart && typeof this.cart.clearOptimisticAdd === 'function') {
              this.cart.clearOptimisticAdd();
            }
            this.resetSubmitState();
            window.location = routes.cart_url;
          })
          .finally(() => {
            this.submitButton.classList.remove("loading");
            if (this.cart && this.cart.classList.contains("is-empty"))
              this.cart.classList.remove("is-empty");
            if (!this.error) this.submitButton.removeAttribute("aria-disabled");
            this.querySelector(".loading__spinner")?.classList.add("hidden");
          });
      }

      // Last line of defence for the P0 "silent add" bug: renderContents opens
      // the drawer asynchronously (setTimeout + rAF). If, a beat later, nothing
      // visible has happened, fall back to the cart page rather than leaving the
      // shopper staring at a page that looks like their tap was ignored.
      confirmVisibleFeedback() {
        setTimeout(() => {
          const drawer = this.cart;
          if (!drawer || !drawer.isConnected) return;
          // cart-drawer marks itself .active; cart-notification marks an inner
          // node instead, so check both.
          const opened =
            drawer.classList.contains("active") ||
            drawer.hasAttribute("open") ||
            !!drawer.querySelector(".active");
          if (opened) return;
          /* OWNER FIX 2026-07-30 (second pass): "we should never be sent to the cart
             page... kill all the things that connect to that." The /cart fallback is
             GONE. The drawer is the only cart surface. If it did not open we keep
             retrying it rather than navigating anywhere, because a page jump to /cart
             loses the gifts, the discount field and the quiz offer. */
          const forceOpen = (attempt) => {
            try {
              if (typeof drawer.open === "function") drawer.open();
            } catch (e) {
              console.error("product-form: forced drawer open failed", e);
            }
            if (attempt >= 3) return;
            setTimeout(() => {
              const nowOpen =
                drawer.classList.contains("active") ||
                drawer.hasAttribute("open") ||
                !!drawer.querySelector(".active");
              if (!nowOpen) forceOpen(attempt + 1);
            }, 350);
          };
          forceOpen(0);
        }, 700);
      }

      getUpsellProductConfig() {
        const upsellFormData = new FormData();
        upsellFormData.append("id", this.upsellVariantId);
        upsellFormData.append("quantity", 1);

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];
        config.body = upsellFormData;

        return config;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(".product-form__error-message-wrapper");
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            ".product-form__error-message"
          );

        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute("disabled", "disabled");
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute("disabled");
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector("[name=id]");
      }
    }
  );
}
